import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { bannerService } from '../../services/bannerService';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    imageUrl: '',
    redirectLink: '',
    order: 0,
    active: true,
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await bannerService.getAdminBanners();
      setBanners(response.data);
    } catch (error) {
      toast.error('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (id) => {
    try {
      await bannerService.toggleBannerActive(id);
      toast.success('Banner status updated');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to update banner status');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await bannerService.deleteBanner(id);
        toast.success('Banner deleted successfully');
        fetchBanners();
      } catch (error) {
        toast.error('Failed to delete banner');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      imageUrl: '',
      redirectLink: '',
      order: 0,
      active: true,
    });
  };

  const handleAddBanner = async (event) => {
    event.preventDefault();

    if (!formData.title.trim() || !formData.imageUrl.trim()) {
      toast.error('Title and image URL are required');
      return;
    }

    try {
      await bannerService.createBanner({
        title: formData.title,
        image: { url: formData.imageUrl },
        redirectLink: formData.redirectLink,
        active: formData.active,
        order: Number(formData.order),
      });
      toast.success('Banner created successfully');
      setShowAddModal(false);
      resetForm();
      fetchBanners();
    } catch (error) {
      toast.error('Failed to create banner');
    }
  };

  if (loading) {
    return <div className="text-center py-20">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Banner Management</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <FaPlus /> Add Banner
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {banners.map((banner) => (
          <div key={banner._id} className="card overflow-hidden">
            <img
              src={banner.image?.url}
              alt={banner.title}
              className="w-full h-48 object-cover"
            />
            <div className="p-4">
              <h3 className="font-semibold mb-2">{banner.title}</h3>
              <div className="flex items-center justify-between mb-4">
                <span className={`badge ${
                  banner.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {banner.active ? 'Active' : 'Inactive'}
                </span>
                <span className="text-sm text-gray-500">Order: {banner.order}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleActive(banner._id)}
                  className="flex-1 btn-secondary text-sm"
                >
                  {banner.active ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => handleDelete(banner._id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-lg p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Add Banner</h2>
              <button
                type="button"
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-900 dark:hover:text-white"
              >
                Close
              </button>
            </div>
            <form onSubmit={handleAddBanner} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg bg-gray-50 dark:bg-gray-800"
                  placeholder="Enter banner title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Image URL</label>
                <input
                  type="url"
                  value={formData.imageUrl}
                  onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg bg-gray-50 dark:bg-gray-800"
                  placeholder="Enter image URL"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Redirect Link</label>
                <input
                  type="url"
                  value={formData.redirectLink}
                  onChange={(e) => setFormData({ ...formData, redirectLink: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg bg-gray-50 dark:bg-gray-800"
                  placeholder="Enter redirect URL"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Order</label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: Number(e.target.value) })}
                    className="w-full px-4 py-3 border rounded-lg bg-gray-50 dark:bg-gray-800"
                    min="0"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    id="activeBanner"
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                  />
                  <label htmlFor="activeBanner" className="text-sm font-medium">
                    Active
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Banner
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerManagement;
